import React, { useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../../components/formlib/MediumTypography';
import { Box } from '@mui/system';
import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { ReactComponent as AddIcon } from '../../../assets/images/CircleAdd.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/deleteAlertIcon.svg';
import { Formik } from 'formik';
import * as yup from 'yup';
import InformationModal from '../../ifsp/InformationModal';
import ModalPopup from '../../../components/formlib/ModalPopup';
import Textinput from '../../../components/formlib/Textinput';
import { AlertColor, Grid, Tooltip } from '@mui/material';
import CustomRadioButton from '../../../components/formlib/CustomRadioButton';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { initialDefaultRoutine } from '../IfspMainScreen';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import {
  addOrUpdateIfspDailyRoutineReport,
  deleteDailyRoutineReport,
  deleteIfspReportCustomRoutine,
  getIfspDailyRoutineReport,
} from '../../../services/ifspClient/IfspApi';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import {
  DailyRoutineType,
  RoutineItemType,
  setIfspDailyRoutineReport,
  updateIfspDailyRoutineReport,
  updateOriginalIfspDailyRoutineReport,
} from '../../../redux/features/ifsp/ifspDailyRoutineSlice';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import dayjs from 'dayjs';
import IfspHeld from '../ifspComponents/IfspHeld';
import { updateIfspUnsavedFields } from '../../../redux/features/ifsp/ifspUnsavedFieldSlice';

const validationSchema = yup.object().shape({
  newRoutine: yup.string().required('IFSP.Routineisrequired'),
});

const feedbackCount = [1, 2, 3];

const DailyRoutineReport = () => {
  const ifspDailyRoutineReportDetails = useAppSelector(
    (state) => state.ifspDailyRoutineReportDetails.ifspDailyRoutineReport,
  );
  const ifspOriginalReport = useAppSelector(
    (state) =>
      state.ifspDailyRoutineReportDetails.originalIfspDailyRoutineReport,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const dispatch = useAppDispatch();
  const [allRoutines, setAllRoutines] = useState<RoutineItemType[]>([]);
  const [deleteCustomRoutineIndexAndId, setDeleteCustomRoutineIndexAndId] =
    useState<{ index: number; id: string }>({
      index: 0,
      id: '',
    });
  const [deleteWarning, setDeleteWarning] = useState<boolean>(false);
  const [deleteNewSection, setDeleteNewSection] = useState<boolean>(false);

  const [openAccordianIndex, setOpenAccordianIndex] = useState<number>(0);
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [infoModal, setInfoModal] = useState(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;

  const { isDailyRoutineChanged, isLastDailyRoutineChangedAndNoCreatedDate } =
    useIfspUnsavedChanges();

  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );

  useEffect(() => {
    const current =
      ifspDailyRoutineReportDetails?.dailyRoutines?.[openAccordianIndex];
    if (!current) return;

    setAllRoutines([
      ...(current.routines || []),
      ...(current.customRoutines || []),
    ]);
  }, [
    openAccordianIndex,
    JSON.stringify(
      ifspDailyRoutineReportDetails?.dailyRoutines?.[openAccordianIndex]
        ?.routines,
    ),
    JSON.stringify(
      ifspDailyRoutineReportDetails?.dailyRoutines?.[openAccordianIndex]
        ?.customRoutines,
    ),
  ]);

  useEffect(() => {
    toggleLoader(true);
    handleGetIfspReport();
  }, [ifspHeatBeatDetails.dailyRoutineEmpId]);

  const handleGetIfspReport = () => {
    return getIfspDailyRoutineReport()
      .then((res) => {
        dispatch(
          setIfspDailyRoutineReport({
            ...res,
            dailyRoutines: res.dailyRoutines.map((item) => ({
              ...item,
              routines:
                item.routines.length === 0
                  ? initialDefaultRoutine
                  : item.routines,
            })),
          }),
        );
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.ReportDetails.GetError');
        setToastrDefaultMessage('Failed to get IFSP report data');
      })
      .finally(() => toggleLoader(false));
  };

  const generateRoutineName = (name: string): string => {
    switch (name) {
      case 'wakingUp':
        return 'Waking Up';
      case 'mealTime':
        return 'Meal Time';
      case 'napTime':
        return 'Nap Time';
      case 'playTime':
        return 'Play Time';
      case 'downTime':
        return 'Down time/hanging out';
      case 'bathTime':
        return 'Bath Time';
      case 'bedTime':
        return 'Bed Time';
      case 'familyActivities':
        return 'Family activities';
      case 'socialGatherings':
        return 'Social and community gatherings';
      case 'comingFromHome':
        return 'Coming and going from home';
      case 'dropOff':
        return 'Drop off/Pick up';
      case 'runningErrands':
        return 'Running errands';
      case 'workSchedule':
        return 'Work/School schedule';
      case 'childCare':
        return 'Child care';
      case 'doctorAppointment':
        return "Doctor's (or other) appointments";

      // If more names come in future, return original
      default:
        return name;
    }
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const addRoutine = (newValue: string) => {
    dispatch(
      updateIfspDailyRoutineReport({
        ...ifspDailyRoutineReportDetails,
        dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.map(
          (item, i) => {
            if (i === openAccordianIndex) {
              return {
                ...item,
                customRoutines: [
                  ...item.customRoutines,
                  {
                    id: '',
                    name: newValue,
                    value: '',
                    comment: '',
                  },
                ],
              };
            }
            return item;
          },
        ),
      }),
    );
  };

  const getDeleteCustomRoutineDetails = (index: number, id: string) => {
    setDeleteCustomRoutineIndexAndId({
      index,
      id,
    });
    setDeleteWarning(true);
  };

  const handleDeleteCustomRoutine = () => {
    toggleLoader(true);
    if (deleteCustomRoutineIndexAndId.id) {
      deleteIfspReportCustomRoutine(deleteCustomRoutineIndexAndId.id)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.DailyRoutine.DeleteSuccess');
          setToastrDefaultMessage('Custom routine deleted successfully');
          const updateDailyRoutines = (dailyRoutines: DailyRoutineType[]) =>
            dailyRoutines.map((item, i) => {
              if (i === openAccordianIndex) {
                return {
                  ...item,
                  customRoutines: item.customRoutines.filter(
                    (routine, j) =>
                      j !== deleteCustomRoutineIndexAndId.index - 15,
                  ),
                };
              }
              return item;
            });

          dispatch(
            updateOriginalIfspDailyRoutineReport({
              ...ifspOriginalReport,
              dailyRoutines: updateDailyRoutines(
                ifspOriginalReport.dailyRoutines,
              ),
            }),
          );

          dispatch(
            updateIfspDailyRoutineReport({
              ...ifspDailyRoutineReportDetails,
              dailyRoutines: updateDailyRoutines(
                ifspDailyRoutineReportDetails.dailyRoutines,
              ),
            }),
          );

          setDeleteWarning(false);
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.DailyRoutine.DeleteError');
          setToastrDefaultMessage('Failed to delete custom routine');
        })
        .finally(() => {
          setDeleteWarning(false);
          toggleLoader(false);
        });
    } else {
      dispatch(
        updateIfspDailyRoutineReport({
          ...ifspDailyRoutineReportDetails,
          dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.map(
            (item, i) => {
              if (i === openAccordianIndex) {
                return {
                  ...item,
                  customRoutines: item.customRoutines.filter(
                    (routine, j) =>
                      j !== deleteCustomRoutineIndexAndId.index - 15,
                  ),
                };
              }
              return item;
            },
          ),
        }),
      );
      setDeleteWarning(false);
      toggleLoader(false);
    }
  };

  const handleRadioButtonChange = (index: number, selectedValue: string) => {
    dispatch(
      updateIfspDailyRoutineReport({
        ...ifspDailyRoutineReportDetails,
        dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.map(
          (item, i) => {
            if (i === openAccordianIndex) {
              return {
                ...item,
                routines: ifspDailyRoutineReportDetails.dailyRoutines[
                  openAccordianIndex
                ].routines.map((routine, j) => {
                  if (j === index) {
                    return {
                      ...routine,
                      value: selectedValue,
                    };
                  }
                  return routine;
                }),
                customRoutines: ifspDailyRoutineReportDetails.dailyRoutines[
                  openAccordianIndex
                ].customRoutines.map((routine, j) => {
                  if (j === index - 15) {
                    return {
                      ...routine,
                      value: selectedValue,
                    };
                  }
                  return routine;
                }),
              };
            }
            return item;
          },
        ),
      }),
    );
  };

  const handleCommentChange = (index: number, value: string) => {
    console.log('index', index, 'value', value);
  };

  const createNewSection = () => {
    dispatch(
      updateIfspDailyRoutineReport({
        ...ifspDailyRoutineReportDetails,
        dailyRoutines: [
          ...ifspDailyRoutineReportDetails.dailyRoutines,
          {
            id: '',
            routines: initialDefaultRoutine,
            customRoutines: [],
            easiestRoutine1: null,
            easiestRoutine2: null,
            easiestRoutine3: null,
            hardestRoutine1: null,
            hardestRoutine2: null,
            hardestRoutine3: null,
            easiestRoutine1Comment: null,
            easiestRoutine2Comment: null,
            easiestRoutine3Comment: null,
            hardestRoutine1Comment: null,
            hardestRoutine2Comment: null,
            hardestRoutine3Comment: null,
            sessionReviewedDate: null,
          },
        ],
      }),
    );
    dispatch(
      updateOriginalIfspDailyRoutineReport({
        ...ifspOriginalReport,
        dailyRoutines: [
          ...ifspOriginalReport.dailyRoutines,
          {
            id: '',
            routines: initialDefaultRoutine,
            customRoutines: [],
            easiestRoutine1: null,
            easiestRoutine2: null,
            easiestRoutine3: null,
            hardestRoutine1: null,
            hardestRoutine2: null,
            hardestRoutine3: null,
            easiestRoutine1Comment: null,
            easiestRoutine2Comment: null,
            easiestRoutine3Comment: null,
            hardestRoutine1Comment: null,
            hardestRoutine2Comment: null,
            hardestRoutine3Comment: null,
            sessionReviewedDate: null,
          },
        ],
      }),
    );
  };

  const deleteSection = () => {
    toggleLoader(true);
    const dailyRoutinesId =
      ifspDailyRoutineReportDetails.dailyRoutines[
        ifspDailyRoutineReportDetails.dailyRoutines.length - 1
      ].id;
    if (dailyRoutinesId) {
      setOpen(true);
      setToastrVariable('success');
      setToastrId('IFSP.DailyRoutineSection.DeleteSuccess');
      setToastrDefaultMessage('Daily routine deleted successfully');
      deleteDailyRoutineReport(dailyRoutinesId)
        .then(() => {
          dispatch(
            updateOriginalIfspDailyRoutineReport({
              dailyRoutines: ifspOriginalReport.dailyRoutines.slice(0, -1),
            }),
          );
          dispatch(
            updateIfspDailyRoutineReport({
              dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.slice(
                0,
                -1,
              ),
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.DailyRoutineSection.DeleteError');
          setToastrDefaultMessage('Failed to delete daily routine');
        })
        .finally(() => {
          setDeleteNewSection(false);
          toggleLoader(false);
        });
    } else {
      dispatch(
        updateIfspDailyRoutineReport({
          ...ifspDailyRoutineReportDetails,
          dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.slice(
            0,
            -1,
          ),
        }),
      );
      dispatch(
        updateOriginalIfspDailyRoutineReport({
          ...ifspOriginalReport,
          dailyRoutines: ifspOriginalReport.dailyRoutines.slice(0, -1),
        }),
      );
      setDeleteNewSection(false);
      toggleLoader(false);
    }
  };
  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  const handleOpenInfoModal = () => {
    setInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModal(false);
  };

  const canShowDeleteSectionDailyRoutines = (mainIndex: number): boolean => {
    const lastIndex = ifspDailyRoutineReportDetails.dailyRoutines.length - 1;
    const lastItem = ifspDailyRoutineReportDetails.dailyRoutines[lastIndex];

    return (
      ifspPermission.hasClientEditPermission &&
      !lastItem.sessionReviewedDate &&
      mainIndex === lastIndex &&
      mainIndex > 0
    );
  };

  const getEasiestRoutineValue = (item: number): string => {
    const routine =
      ifspDailyRoutineReportDetails.dailyRoutines[openAccordianIndex];
    switch (item) {
      case 1:
        return routine.easiestRoutine1 || '';
      case 2:
        return routine.easiestRoutine2 || '';
      case 3:
        return routine.easiestRoutine3 || '';
      default:
        return '';
    }
  };

  const getEasiestRoutineComment = (item: number): string => {
    const routine =
      ifspDailyRoutineReportDetails.dailyRoutines[openAccordianIndex];
    switch (item) {
      case 1:
        return routine.easiestRoutine1Comment || '';
      case 2:
        return routine.easiestRoutine2Comment || '';
      case 3:
        return routine.easiestRoutine3Comment || '';
      default:
        return '';
    }
  };

  const getHardestRoutineValue = (item: number): string => {
    const routine =
      ifspDailyRoutineReportDetails.dailyRoutines[openAccordianIndex];
    switch (item) {
      case 1:
        return routine.hardestRoutine1 || '';
      case 2:
        return routine.hardestRoutine2 || '';
      case 3:
        return routine.hardestRoutine3 || '';
      default:
        return '';
    }
  };

  const getHardestRoutineComment = (item: number): string => {
    const routine =
      ifspDailyRoutineReportDetails.dailyRoutines[openAccordianIndex];
    switch (item) {
      case 1:
        return routine.hardestRoutine1Comment || '';
      case 2:
        return routine.hardestRoutine2Comment || '';
      case 3:
        return routine.hardestRoutine3Comment || '';
      default:
        return '';
    }
  };

  const addOrUpdateIfspReportDetails = () => {
    toggleLoader(true);
    addOrUpdateIfspDailyRoutineReport({
      ...ifspDailyRoutineReportDetails,
      dailyRoutines: ifspDailyRoutineReportDetails.dailyRoutines.map(
        (item, index) => {
          const isLastIndex =
            index === ifspDailyRoutineReportDetails.dailyRoutines.length - 1;
          return {
            ...item,
            sessionReviewedDate:
              isLastIndex &&
              isLastDailyRoutineChangedAndNoCreatedDate &&
              !item.sessionReviewedDate
                ? dayjs().format('MM/DD/YYYY hh:mm A')
                : item.sessionReviewedDate,
          };
        },
      ),
    })
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.DailyRoutine.UpdateSuccess');
        setToastrDefaultMessage('IFSP Daily Routine updated successfully');
        return handleGetIfspReport();
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.DailyRoutine.UpdateError');
        setToastrDefaultMessage('Failed to update IFSP Daily Routine');
      })
      .finally(() => toggleLoader(false));
  };

  return (
    <Box>
      {ifspDailyRoutineReportDetails.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspDailyRoutineReportDetails?.heldBy} />
        </Box>
      ) : null}
      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspDailyRoutineReportDetails.isCurrentlyHeld
            ? 'none'
            : 'auto',
          opacity: ifspDailyRoutineReportDetails.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspDailyRoutineReportDetails.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspDailyRoutineReportDetails.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <Formik
          initialValues={{ newRoutine: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
            resetForm();
            dispatch(
              updateIfspUnsavedFields({
                ...ifspUnsavedFields,
                isOtherDailyRoutineFieldsChanged: false,
              }),
            );
            addRoutine(values.newRoutine);
          }}
        >
          {({
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            resetForm,
          }) => (
            <Box component="div" className="ml-md mr-md mb-md">
              <Box
                sx={{
                  border: '1px solid #008C82',
                  borderBottomLeftRadius: '4px',
                  borderBottomRightRadius: '4px',
                  borderTop: 'none',
                }}
                className="p-sm"
              >
                <SnackBarComponent
                  open={open}
                  handleClose={handleCloseSnakBar}
                  successOrError={toastrVariable}
                  labelId={toastrId}
                  defaultMessageId={toastrDefaultMessage}
                />
                <InformationModal
                  open={infoModal}
                  titleLabel="IFSP.Information"
                  titleDefaultLabel="Information"
                  buttonLabel="IFSP.InfoOkButton"
                  buttonDefaultLabel="Ok"
                  onClose={handleCloseInfoModal}
                  infoLabelId="IFSP.ChildOutcomesInfo"
                  infoDefaultLabel="Child Outcomes are a way to measure a child's progress as a result of their participation in an Early Intervention Program. Early Intervention supports children in the achievement of three Federal Child Outcomes: (1) developing positive social-emotional skills (including social relationships); (2) acquiring and using their knowledge and skills (including early language/communication); and (3) using appropriate behaviors to meet their needs. Information gathered from daily routines is important when determining a child's progress in each of these areas as they become active participants at home and in the community. \n The three Federal Child Outcomes refer to actions that children need to be able to carry out and knowledge that children need to use in order to function successfully across a variety of settings. To be successful in these settings, it is important for children to be able to, for example, get along with others, follow the rules in a group, continue to learn new things, and take care of their basic needs in an appropriate way. Ultimately, achieving these outcomes will help children thrive at home, in school, and in many settings throughout their communities."
                />
                <ModalPopup
                  open={openUnSavedModal}
                  description="UnsavedAlertDescriptionWhileLeaving"
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="Cancel"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                  onCancel={() => {
                    setOpenUnSavedModal(false);
                  }}
                  onOk={() => {
                    dispatch(setIfspDailyRoutineReport(ifspOriginalReport));
                    dispatch(
                      updateIfspUnsavedFields({
                        ...ifspUnsavedFields,
                        isOtherDailyRoutineFieldsChanged: false,
                      }),
                    );
                    resetForm();
                    setOpenUnSavedModal(false);
                  }}
                />
                <ModalPopup
                  open={deleteWarning}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    handleDeleteCustomRoutine();
                  }}
                  onCancel={() => {
                    setDeleteWarning(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                <ModalPopup
                  open={deleteNewSection}
                  description="permanentDeleteConfirmationMessage"
                  onOk={() => {
                    deleteSection();
                  }}
                  onCancel={() => {
                    setDeleteNewSection(false);
                  }}
                  labelId1="Clientpage.cancelbtn"
                  negativeActionLabel="cancelText"
                  labelId2="Clientpage.Okbtn"
                  positiveActionLabel="Ok"
                />
                {ifspDailyRoutineReportDetails.dailyRoutines.map(
                  (ifspData, mainIndex) => (
                    <Box
                      component="section"
                      className="p-lg borderRadius4 mb-lg"
                      sx={{
                        border: '1px solid #00C6B8',
                        backgroundColor: '#ECF9F8',
                      }}
                      key={ifspData.id}
                    >
                      <Box
                        component="div"
                        className="flex__justify__space-between"
                      >
                        {openAccordian && mainIndex === openAccordianIndex ? (
                          <Box
                            component="div"
                            className="flex__justify__space-between"
                          >
                            <MediumTypography
                              labelid="IFSP.session1"
                              defaultlabel="Daily Routines, Strengths and Needs"
                              fontSize="18px"
                              fontweight={600}
                              textColor="#2A4241"
                              className="pr-md"
                            />
                            <InfoIcon
                              className="cursorPointer"
                              onClick={handleOpenInfoModal}
                            />
                            {ifspDailyRoutineReportDetails.dailyRoutines[
                              mainIndex
                            ].sessionReviewedDate ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginLeft: '24px',
                                  backgroundColor: '#97A6A5',
                                  alignItems: 'center',
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.UpdatedOn"
                                  defaultlabel="Created On: "
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                />
                                <MediumTypography
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                  className="pl-xs"
                                  label={ifspData.sessionReviewedDate as string}
                                />
                              </Box>
                            ) : null}
                          </Box>
                        ) : (
                          <Box className="flex__justify__space-between">
                            <MediumTypography
                              labelid="IFSP.session1"
                              defaultlabel="Daily Routines, Strengths and Needs"
                              fontSize="18px"
                              fontweight={600}
                              textColor="#2A4241"
                            />
                            {ifspDailyRoutineReportDetails.dailyRoutines[
                              mainIndex
                            ].sessionReviewedDate ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  padding: '4px 8px 4px 8px',
                                  borderRadius: '4px',
                                  marginLeft: '24px',
                                  backgroundColor: '#97A6A5',
                                  alignItems: 'center',
                                }}
                              >
                                <MediumTypography
                                  labelid="IFSP.UpdatedOn"
                                  defaultlabel="Created On: "
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                />
                                <MediumTypography
                                  fontSize="14px"
                                  fontweight={400}
                                  textColor="#fff"
                                  className="pl-xs"
                                  label={ifspData.sessionReviewedDate as string}
                                />
                              </Box>
                            ) : null}
                          </Box>
                        )}
                        <Box className="pt-xs">
                          {openAccordian && mainIndex === openAccordianIndex ? (
                            <Box className="flex__ align__items__center">
                              {ifspPermission.hasClientEditPermission &&
                              ifspDailyRoutineReportDetails.dailyRoutines[
                                ifspDailyRoutineReportDetails.dailyRoutines
                                  .length - 1
                              ].sessionReviewedDate &&
                              mainIndex ===
                                ifspDailyRoutineReportDetails.dailyRoutines
                                  .length -
                                  1 ? (
                                <Box
                                  className="cursorPointer"
                                  sx={{
                                    padding: '4px 8px 4px 8px',
                                    borderRadius: '4px',
                                    marginRight: '24px',
                                    border: '1px solid #008C82',
                                    backgroundColor: '#fff',
                                  }}
                                  onClick={() => {
                                    createNewSection();
                                  }}
                                >
                                  <MediumTypography
                                    labelid="IFSP.AddNewSection"
                                    defaultlabel="+ Add New"
                                    fontSize="12px"
                                    fontweight={600}
                                  />
                                </Box>
                              ) : (
                                canShowDeleteSectionDailyRoutines(
                                  mainIndex,
                                ) && (
                                  <Box
                                    component={'div'}
                                    className="cursorPointer flex__ align__items__center pr-md"
                                    onClick={() => {
                                      setDeleteNewSection(true);
                                    }}
                                    sx={{
                                      padding: '4px 8px 4px 8px',
                                      borderRadius: '4px',
                                      marginRight: '24px',
                                      border: '1px solid #EB4C60',
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    <DeleteIcon className="pr-xs" />
                                    <MediumTypography
                                      labelid="IFSP.DeleteNewSection"
                                      defaultlabel="Delete"
                                      fontSize="12px"
                                      fontweight={600}
                                    />
                                  </Box>
                                )
                              )}
                              <UpArrow
                                className="cursorPointer"
                                onClick={() => {
                                  setOpenAccordianIndex((prevIndex) => {
                                    return prevIndex;
                                  });
                                  setOpenAccordian(false);
                                }}
                              />
                            </Box>
                          ) : (
                            <Box className="flex__ align__items__center">
                              {ifspPermission.hasClientEditPermission &&
                              ifspDailyRoutineReportDetails.dailyRoutines[
                                ifspDailyRoutineReportDetails.dailyRoutines
                                  .length - 1
                              ].sessionReviewedDate &&
                              mainIndex ===
                                ifspDailyRoutineReportDetails.dailyRoutines
                                  .length -
                                  1 ? (
                                <Box
                                  className="cursorPointer"
                                  sx={{
                                    padding: '4px 8px 4px 8px',
                                    borderRadius: '4px',
                                    marginRight: '24px',
                                    border: '1px solid #008C82',
                                    backgroundColor: '#fff',
                                  }}
                                  onClick={() => {
                                    createNewSection();
                                  }}
                                >
                                  <MediumTypography
                                    labelid="IFSP.AddNewSection"
                                    defaultlabel="+ Add New"
                                    fontSize="12px"
                                    fontweight={600}
                                  />
                                </Box>
                              ) : (
                                canShowDeleteSectionDailyRoutines(
                                  mainIndex,
                                ) && (
                                  <Box
                                    component={'div'}
                                    className="cursorPointer flex__ align__items__center pr-md"
                                    onClick={() => {
                                      setDeleteNewSection(true);
                                    }}
                                    sx={{
                                      padding: '4px 8px 4px 8px',
                                      borderRadius: '4px',
                                      marginRight: '24px',
                                      border: '1px solid #EB4C60',
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    <DeleteIcon className="pr-xs" />
                                    <MediumTypography
                                      labelid="IFSP.DeleteNewSection"
                                      defaultlabel="Delete"
                                      fontSize="12px"
                                      fontweight={600}
                                    />
                                  </Box>
                                )
                              )}
                              <DownArrow
                                className="cursorPointer"
                                onClick={() => {
                                  setOpenAccordianIndex(mainIndex);
                                  setOpenAccordian(true);
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {openAccordian && mainIndex === openAccordianIndex && (
                        <Box component="div" className="mt-lg">
                          <Box
                            ref={parentRef}
                            style={{
                              pointerEvents:
                                !ifspPermission.hasClientEditPermission
                                  ? 'none'
                                  : 'auto',
                              opacity: !ifspPermission.hasClientEditPermission
                                ? '0.5'
                                : '1 ',
                            }}
                            onKeyDownCapture={(e) => {
                              if (!ifspPermission.hasClientEditPermission) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                            onFocus={() => {
                              if (!ifspPermission.hasClientEditPermission) {
                                if (parentRef.current)
                                  parentRef.current.focus();
                              }
                            }}
                          >
                            <Grid
                              container
                              className="pt-md pb-md"
                              sx={{
                                borderBottomWidth: '1px',
                                borderBottomStyle: 'solid',
                                borderBottomColor: '#CFDEDE',
                              }}
                            >
                              <Grid item xs={3}>
                                <MediumTypography
                                  labelid="IFSP.routine"
                                  defaultlabel="Routines"
                                  fontweight={700}
                                  textColor="#97A6A5"
                                />
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                className="align__items__center"
                              >
                                <MediumTypography
                                  labelid="IFSP.easy"
                                  defaultlabel="Easy"
                                  fontweight={700}
                                  textstyle="center"
                                  textColor="#97A6A5"
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <MediumTypography
                                  labelid="IFSP.notEasy"
                                  defaultlabel="Not Easy"
                                  fontweight={700}
                                  textstyle="center"
                                  textColor="#97A6A5"
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <MediumTypography
                                  labelid="IFSP.comments"
                                  defaultlabel="Comments"
                                  fontweight={700}
                                  textstyle="left"
                                  textColor="#97A6A5"
                                />
                              </Grid>
                            </Grid>
                            {allRoutines.map((routines, index) => (
                              <Grid
                                container
                                className="pt-md pb-md"
                                sx={{
                                  borderBottomWidth: '1px',
                                  borderBottomStyle: 'solid',
                                  borderBottomColor: '#CFDEDE',
                                }}
                                key={routines.name}
                              >
                                <Grid
                                  item
                                  xs={3}
                                  className="flex__ align__items__center"
                                >
                                  <Tooltip
                                    title={generateRoutineName(
                                      routines.name || '',
                                    )}
                                    arrow={true}
                                    followCursor
                                  >
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        width: '400px',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <MediumTypography
                                        label={generateRoutineName(
                                          routines.name || '',
                                        )}
                                        fontweight={500}
                                        textColor="#2A4241"
                                        sxProps={{
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                </Grid>
                                <Grid
                                  item
                                  xs={2}
                                  className="flex__ flex__justify__center align__items__center pl-xlg"
                                >
                                  <CustomRadioButton
                                    data={[{ value: 'easy' }]}
                                    value={routines.value}
                                    onSelectItem={(selectedValue) => {
                                      if (selectedValue) {
                                        handleRadioButtonChange(
                                          index,
                                          selectedValue,
                                        );
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  xs={2}
                                  className="flex__ flex__justify__center align__items__center pl-xlg"
                                >
                                  <CustomRadioButton
                                    data={[{ value: 'not-easy' }]}
                                    value={routines.value}
                                    onSelectItem={(selectedValue) => {
                                      if (selectedValue) {
                                        handleRadioButtonChange(
                                          index,
                                          selectedValue,
                                        );
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  xs={4}
                                  className="flex__ flex__justify__center align__items__center"
                                >
                                  <Textinput
                                    className="bg__white"
                                    labelid="IFSP.comments"
                                    defaultlabelid="Comments"
                                    Value={routines.comment}
                                    inputProps={{
                                      className: 'translate',
                                      maxLength: 500,
                                    }}
                                    handlechange={(text) =>
                                      handleCommentChange(index, text)
                                    }
                                    sxProps={{ width: '400px' }}
                                    disabled={true}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  xs={1}
                                  className="flex__ justifyContent-FlexEnd align__items__center"
                                >
                                  {index > 14 && (
                                    <Box
                                      className="cursorPointer flex__"
                                      onClick={() =>
                                        getDeleteCustomRoutineDetails(
                                          index,
                                          routines.id || '',
                                        )
                                      }
                                    >
                                      <DeleteIcon className="cursorPointer " />
                                      <MediumTypography
                                        labelid="IFSP.Delete"
                                        defaultlabel="Delete"
                                        className="ml-xs"
                                        sxProps={{
                                          color: '#EB4C60',
                                          fontWeight: 700,
                                          fontSize: '16px',
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Grid>
                              </Grid>
                            ))}
                            <Box component="div" className="flex__ mt-lg">
                              <Textinput
                                className="bg__white mr-md"
                                labelid="IFSP.enterOtherRoutine"
                                defaultlabelid="Enter Other Routine"
                                Value={values.newRoutine}
                                inputProps={{
                                  className: 'translate',
                                  maxLength: 500,
                                }}
                                handlechange={(text) => {
                                  if (text) {
                                    setFieldValue('newRoutine', text);
                                    dispatch(
                                      updateIfspUnsavedFields({
                                        ...ifspUnsavedFields,
                                        isOtherDailyRoutineFieldsChanged: true,
                                      }),
                                    );
                                  } else {
                                    setFieldValue('newRoutine', '');
                                    dispatch(
                                      updateIfspUnsavedFields({
                                        ...ifspUnsavedFields,
                                        isOtherDailyRoutineFieldsChanged: false,
                                      }),
                                    );
                                  }
                                }}
                                sxProps={{ width: '400px' }}
                              />
                              <Box
                                component="div"
                                className="flex__ align__items__center cursorPointer"
                                onClick={() => handleSubmit()}
                              >
                                <AddIcon className="cursorPointer mr-sm" />
                                <MediumTypography
                                  labelid="IFSP.Add"
                                  defaultlabel="Add"
                                  fontweight={400}
                                  textColor="#2A4241"
                                />
                              </Box>
                            </Box>
                            {errors.newRoutine && touched.newRoutine && (
                              <MediumTypography
                                className="errorText-md"
                                labelid={errors.newRoutine}
                                defaultlabel="Routine is Required"
                              />
                            )}
                            <Box component="div" className="mt-lg">
                              <Grid container columnGap={'40px'}>
                                <Grid item xs={0.01} />
                                <Grid item xs={4.5}>
                                  <MediumTypography
                                    labelid="IFSP.easiestRoutine"
                                    defaultlabel="Easiest or most enjoyable times (list top 3 routines)"
                                  />
                                </Grid>
                                <Grid item xs={4.5}>
                                  <MediumTypography
                                    labelid="IFSP.whyWhatEasy"
                                    defaultlabel="Why? What makes this time go so well? Who is involved?"
                                  />
                                </Grid>
                              </Grid>
                              {feedbackCount.map((item) => (
                                <Grid
                                  key={feedbackCount[item]}
                                  container
                                  columnGap={'40px'}
                                  className="pt-sm"
                                >
                                  <Grid item xs={0.01} className="pt-md">
                                    <MediumTypography
                                      label={'0' + item.toString()}
                                      fontweight={600}
                                      textColor="#00C6B8"
                                    />
                                  </Grid>
                                  <Grid item xs={4.5}>
                                    <CommentTextbox
                                      className="bg__white"
                                      placeholder="Routine"
                                      maxLength={500}
                                      Value={getEasiestRoutineValue(item)}
                                      handlechange={(text) => {
                                        dispatch(
                                          updateIfspDailyRoutineReport({
                                            ...ifspDailyRoutineReportDetails,
                                            dailyRoutines:
                                              ifspDailyRoutineReportDetails.dailyRoutines.map(
                                                (items, index) => {
                                                  if (
                                                    index === openAccordianIndex
                                                  ) {
                                                    return {
                                                      ...items,
                                                      ...(item === 1 && {
                                                        easiestRoutine1:
                                                          text || null,
                                                      }),
                                                      ...(item === 2 && {
                                                        easiestRoutine2:
                                                          text || null,
                                                      }),
                                                      ...(item === 3 && {
                                                        easiestRoutine3:
                                                          text || null,
                                                      }),
                                                    };
                                                  }
                                                  return items;
                                                },
                                              ),
                                          }),
                                        );
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={4.5}>
                                    <CommentTextbox
                                      className="bg__white"
                                      placeholder="Comments"
                                      maxLength={500}
                                      Value={getEasiestRoutineComment(item)}
                                      handlechange={(text) => {
                                        dispatch(
                                          updateIfspDailyRoutineReport({
                                            ...ifspDailyRoutineReportDetails,
                                            dailyRoutines:
                                              ifspDailyRoutineReportDetails.dailyRoutines.map(
                                                (items, index) => {
                                                  if (
                                                    index === openAccordianIndex
                                                  ) {
                                                    return {
                                                      ...items,
                                                      ...(item === 1 && {
                                                        easiestRoutine1Comment:
                                                          text || null,
                                                      }),
                                                      ...(item === 2 && {
                                                        easiestRoutine2Comment:
                                                          text || null,
                                                      }),
                                                      ...(item === 3 && {
                                                        easiestRoutine3Comment:
                                                          text || null,
                                                      }),
                                                    };
                                                  }
                                                  return items;
                                                },
                                              ),
                                          }),
                                        );
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              ))}
                            </Box>
                            <Box component="div" className="mt-lg">
                              <Grid container columnGap={'40px'}>
                                <Grid item xs={0.01} />
                                <Grid item xs={4.5}>
                                  <MediumTypography
                                    labelid="IFSP.hardestRoutine"
                                    defaultlabel="Hardest or most challenging times (list top 3 routines)"
                                  />
                                </Grid>
                                <Grid item xs={5.5}>
                                  <MediumTypography
                                    labelid="IFSP.whyWhatHard"
                                    defaultlabel="Why? What makes this time so challenging? Who is involved?"
                                  />
                                </Grid>
                              </Grid>
                              {feedbackCount.map((item) => (
                                <Grid
                                  key={feedbackCount[item]}
                                  container
                                  columnGap={'40px'}
                                  className="pt-sm"
                                >
                                  <Grid item xs={0.01} className="pt-md">
                                    <MediumTypography
                                      label={'0' + item.toString()}
                                      className="pr-md"
                                      fontweight={600}
                                      textColor="#00C6B8"
                                    />
                                  </Grid>
                                  <Grid item xs={4.5}>
                                    <CommentTextbox
                                      className="bg__white"
                                      placeholder="Routine"
                                      maxLength={500}
                                      Value={getHardestRoutineValue(item)}
                                      handlechange={(text) => {
                                        dispatch(
                                          updateIfspDailyRoutineReport({
                                            ...ifspDailyRoutineReportDetails,
                                            dailyRoutines:
                                              ifspDailyRoutineReportDetails.dailyRoutines.map(
                                                (items, index) => {
                                                  if (
                                                    index === openAccordianIndex
                                                  ) {
                                                    return {
                                                      ...items,
                                                      ...(item === 1 && {
                                                        hardestRoutine1:
                                                          text || null,
                                                      }),
                                                      ...(item === 2 && {
                                                        hardestRoutine2:
                                                          text || null,
                                                      }),
                                                      ...(item === 3 && {
                                                        hardestRoutine3:
                                                          text || null,
                                                      }),
                                                    };
                                                  }
                                                  return items;
                                                },
                                              ),
                                          }),
                                        );
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={4.5}>
                                    <CommentTextbox
                                      className="bg__white"
                                      placeholder="Comments"
                                      maxLength={500}
                                      Value={getHardestRoutineComment(item)}
                                      handlechange={(text) => {
                                        dispatch(
                                          updateIfspDailyRoutineReport({
                                            ...ifspDailyRoutineReportDetails,
                                            dailyRoutines:
                                              ifspDailyRoutineReportDetails.dailyRoutines.map(
                                                (items, index) => {
                                                  if (
                                                    index === openAccordianIndex
                                                  ) {
                                                    return {
                                                      ...items,
                                                      ...(item === 1 && {
                                                        hardestRoutine1Comment:
                                                          text || null,
                                                      }),
                                                      ...(item === 2 && {
                                                        hardestRoutine2Comment:
                                                          text || null,
                                                      }),
                                                      ...(item === 3 && {
                                                        hardestRoutine3Comment:
                                                          text || null,
                                                      }),
                                                    };
                                                  }
                                                  return items;
                                                },
                                              ),
                                          }),
                                        );
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ),
                )}
                <Box
                  component="div"
                  className="flex__ justifyContent-FlexEnd mb-sm"
                >
                  <ButtonComponent
                    className="btn-primary btn-cancel"
                    labelId={'cancelText'}
                    defaultLabelId="Cancel"
                    variantType="outlined"
                    onClick={() => {
                      if (
                        isDailyRoutineChanged ||
                        ifspUnsavedFields.isOtherDailyRoutineFieldsChanged
                      ) {
                        setOpenUnSavedModal(true);
                      } else {
                        setOpenUnSavedModal(false);
                      }
                    }}
                    disabled={!ifspPermission.hasClientEditPermission}
                  />
                  <ButtonComponent
                    className="btn-primary btn-submit  ml-md"
                    labelId="IFSP.SaveReportRoutines"
                    defaultLabelId="Save Daily Routines, Strengths and Needs"
                    variantType="contained"
                    onClick={() => {
                      addOrUpdateIfspReportDetails();
                    }}
                    disabled={!ifspPermission.hasClientEditPermission}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default DailyRoutineReport;
